import { ComponentType, lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import IntlMessages from "./i18n/intl-messages";
import Root from "./routes";
import Theme from "./theme";

// MSAL imports
import BlockedDevices from "@/components/blocked-devcies";
import { loginRequest } from "@/config/msalConfig";
import {
  clerkPublishableKey,
  isClerkEnabled,
  useCurrentAppAccount,
} from "@/config/clerkConfig";
import { ila26ClerkAppearance } from "@/config/clerkAppearance";
import { classesTenantBox, Icon, LogoIla26, TenantBox, TenantsBox } from "@/styles/style";
import { InteractionType, IPublicClientApplication } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import {
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
  Show,
} from "@clerk/react";
import SignInPage from "@/pages/sign-in";
import ClerkSessionSideEffects from "@/components/clerk-session-side-effects";
import ErrorHandlerBox from "@components/error-handler-box";

import useCurrentUserHook from "@components/user-connection-modal/useCurrentUserHook.ts";
import { faArrowRightToBracket } from "@fortawesome/pro-light-svg-icons";
import { Box, Button, CircularProgress, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { useLDClient, withLDProvider } from "launchdarkly-react-client-sdk";
import { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "./App.css";
import { AbilityContext } from "./casl";
import useAppAbility from "./casl/useAppAbility.ts";
import ServiceUnavailable from "@/components/service-unavailable";
import ForcePasswordChange from "@/components/force-password-change";
import { useAppClerkUser } from "@/config/clerkConfig";
import { isTransientError } from "@/utils/transient-error";
import useCheckDevice from "./hooks/useCheckDevice.ts";
import { RootState } from "./store/index.ts";
import { profileApi } from "./store/profile/endpoints/index.ts";
import { PlanType, TenantTypeEnum } from "./store/tenants/endpoints/tenants.ts";
import { selectTenant, shouldSelectTenantAction } from "./store/tenants/tenantsSlice.ts";
import LanguageCacheProvider from "./components/lang/LanguageCacheProvider.tsx";
import messages from "./components/user-connection-modal/messages.ts";
import { useIntl } from "react-intl";
import useDirection from "./hooks/useDirection.ts";
import { useGetCurrentTenantInfo } from "./hooks/useGetCurrentTenantInfo.ts";
import { useGetCurrentEmployeeQuery } from "./store/employee/endpoints/index.ts";
import GoogleAnalytics from "./utils/google-analytics/index.tsx";
import { getLogo } from "./utils/env/logo.ts";
import { useGetUserByIdQuery } from "./store/profile/user/endpoints/index.ts";

const UserConnectionModal = lazy(() => import("@components/user-connection-modal"));

type AppProps = {
  instance: IPublicClientApplication;
};

const AppLoadingComponent = () => {
  return (
    <Box
      display="flex"
      flex={1}
      justifyContent="center"
      alignItems="center"
      height="100vh"
      flexDirection="column"
      gap={2}
    >
      <CircularProgress color="primary" size={40} />
      <LogoIla26 src={getLogo()} style={{ height: 30 }} />
    </Box>
  );
};

const RootWrapper = () => {
  // Reads the currently signed-in account through the provider-agnostic
  // compat hook (see @/config/clerkConfig). Resolves to MSAL's active
  // account when isClerkEnabled=false and to Clerk's useUser() when true —
  // decided at bundle time, so React sees a stable hook every render.
  const activeAccount = useCurrentAppAccount();
  // Raw Clerk user resource — needed for unsafeMetadata (temp-password
  // migration gate). Same bundle-time provider selection as the compat
  // hooks in clerkConfig: on Clerk-enabled builds this is Clerk's
  // useUser(); on MSAL builds a stable no-op.
  const { user: clerkUser } = useAppClerkUser();

  const { formatMessage: __ } = useIntl();
  const currentDirection = useDirection();

  const { shouldSelectTenant, tenantsList, tenantId } = useSelector(
    (state: RootState) => state.tenant
  );

  const tenantInfo = useGetCurrentTenantInfo();
  const { data: currentEmployee } = useGetCurrentEmployeeQuery();
  const { data: currentUser } = useGetUserByIdQuery(
    { id: currentEmployee?.id as string },
    { skip: !currentEmployee }
  );

  const dispatch = useDispatch();
  const { ability, isLoading, error: abilityError, retryAbility } = useAppAbility();
  const { openUserModal, isLoadingUser } = useCurrentUserHook();
  const [isFirstConnectionModalOpen, setIsFirstConnectionModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoadingUser) {
      setIsFirstConnectionModalOpen(openUserModal);
    }
  }, [isLoadingUser, openUserModal]);

  const isCurrentUserOwner = useMemo(() => {
    // Check if the current user is an owner
    return tenantInfo?.isOwner;
  }, [tenantInfo]);

  const error = useMemo(() => {
    if (abilityError && "status" in abilityError && "data" in abilityError) {
      return {
        ...(abilityError.data as {
          messages: string[];
          source: string;
          exception: string;
          errorId: string;
          supportMessage: string;
          statusCode: number;
          errorCode?: string;
        }),
        status: abilityError.status,
      };
    }
  }, [abilityError]);

  const [selectedTenant, setSelectedTenant] = useState<string>("");

  const handleChange = useCallback((event: SelectChangeEvent) => {
    setSelectedTenant(event.target.value as string);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedTenant && activeAccount) {
      dispatch(selectTenant({ tenantId: selectedTenant, userId: activeAccount.id }));
      dispatch(shouldSelectTenantAction({ shouldSelectTenant: false, tenantsList: tenantsList }));
      profileApi.endpoints.getCurrentProfile.initiate();
    }
  }, [activeAccount, selectedTenant, dispatch, tenantsList]);

  const { isProhibited } = useCheckDevice();

  const ldClient = useLDClient();
  useEffect(() => {
    const currentUserLicenses = currentUser?.licenses.reduce((prev: string[], curr) => {
      if (curr === PlanType.BASIC) {
        prev.push("Basic");
      }
      if (curr === PlanType.BUSINESS) {
        prev.push("Marketplace");
      }
      if (curr === PlanType.ENTERPRISE) {
        prev.push("Enterprise");
      }
      if (curr === PlanType.ICRM) {
        prev.push("ICrm");
      }
      if (curr === PlanType.CRM_VAULT) {
        prev.push("CrmVault");
      }
      return prev;
    }, []);

    tenantInfo &&
      currentUser &&
      currentUserLicenses &&
      ldClient &&
      ldClient.identify({
        kind: "multi",
        user: {
          key: currentUser.id,
          name: `${currentUser.firstName} ${currentUser.lastName}`,
          licenses: currentUserLicenses,
        },
        tenant: {
          name: tenantInfo.tenant.name as string,
          key: tenantId,
          type: tenantInfo.isBasic ? "Personal" : "Commercial",
        },
      });
  }, [tenantId, ldClient, tenantInfo, currentUser]);

  if (isProhibited) return <BlockedDevices />;

  // Transient boot failure (backend redeploy, network blip): keep the
  // session, show the recoverable retry screen instead of any logout flow.
  if (
    isTransientError(abilityError) &&
    !location.pathname.includes("logout-session")
  ) {
    return <ServiceUnavailable onRetry={retryAbility} />;
  }

  // Migrated accounts imported with a temporary password must set their own
  // before entering the app (flag set by the Clerk import script).
  if (
    clerkUser?.unsafeMetadata?.requirePasswordChange === true &&
    !location.pathname.includes("logout-session")
  ) {
    return <ForcePasswordChange />;
  }

  if (error && !location.pathname.includes("logout-session")) {
    switch (error.errorCode) {
      case "tenant_not_found":
        return <ErrorHandlerBox errorType={"enterprise"} />;
      case "token_not_found":
        return <ErrorHandlerBox errorType={"enterprise"} />;
      case "already_member":
        return <ErrorHandlerBox errorType={"enterprise"} />;
      case "invitation_already_accepted":
        return <ErrorHandlerBox errorType={"invitation"} />;
      case "tenant_subscription_expired":
        return <ErrorHandlerBox errorType={"subscriptionExpired"} />;
    }
  }

  if (isLoading && !location.pathname.includes("logout-session")) {
    return <AppLoadingComponent />;
  }

  if (
    shouldSelectTenant &&
    tenantsList &&
    tenantsList.length > 1 &&
    !location.pathname.startsWith("/invitations") // alow user to accept invitation before selecting tenant (uses startsWith to distinct from other /invitations routes e.g. network-ila26/my-network/invitations)
  ) {
    return (
      <Stack justifyContent="center" alignItems="center" height="100vh" px="30%" spacing={2}>
        <LogoIla26 src={getLogo()} />
        <Typography>{__(messages.selectCompanyToContinue)}</Typography>
        <TenantsBox>
          {tenantsList
            .filter(tenant => tenant.tenantType !== TenantTypeEnum.Personal)
            .map(item => (
              <TenantBox
                className={selectedTenant === item.id ? classesTenantBox.selected : ""}
                key={item.id}
                onClick={() =>
                  handleChange({
                    target: { value: item.id },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              >
                <Icon
                  icon={faArrowRightToBracket}
                  style={{
                    rotate: currentDirection === "rtl" ? "180deg" : "0deg",
                  }}
                />
                {item.name}
              </TenantBox>
            ))}
          <Box sx={{ textAlign: "right" }}>
            <Button variant={"contained"} onClick={handleSubmit} disabled={selectedTenant === ""}>
              {__(messages.continue)}
            </Button>
          </Box>
        </TenantsBox>
      </Stack>
    );
  }

  if (openUserModal && isCurrentUserOwner && !location.pathname.includes("logout-session")) {
    // Flag for operation that need to be done upon first sign in
    localStorage.setItem("first-connection", "true");

    return (
      <Suspense fallback={<AppLoadingComponent />}>
        <UserConnectionModal
          open={isFirstConnectionModalOpen}
          handleClose={() => setIsFirstConnectionModalOpen(false)}
        />
      </Suspense>
    );
  }

  return (
    <AbilityContext.Provider value={ability}>
      <Root />
    </AbilityContext.Provider>
  );
};

function App({ instance }: AppProps) {
  const authRequest = {
    ...loginRequest,
    forceRefresh: true,
    refreshTokenExpirationOffsetSeconds: 86400,
  };

  // The provider-agnostic app body. Mounted identically under either the
  // MSAL or the Clerk gate so nothing inside changes between providers.
  const appBody = (
    <IntlMessages>
      <LanguageCacheProvider>
        <Theme>
          <RootWrapper />
          <Toaster
            position="top-center"
            reverseOrder={false}
            toastOptions={{
              className: "react-hot-toast",
            }}
          />
          <GoogleAnalytics />
        </Theme>
      </LanguageCacheProvider>
    </IntlMessages>
  );

  // Phase 6 (Azure B2C -> Clerk migration): when VITE_CLERK_PUBLISHABLE_KEY
  // is set at build time, ClerkProvider owns the auth flow. <SignedIn>
  // gates the app body; <SignedOut> renders the branded SignInPage inline
  // (routing="virtual" inside SignInPage keeps the browser URL, so the
  // deep link the user hit is preserved after sign-in).
  // <ClerkSessionSideEffects> mirrors the semantic core of MSAL's
  // logout/token-failure callbacks in main.tsx (tenant-slice + RTK cache
  // reset). When VITE_CLERK_PUBLISHABLE_KEY is unset the MSAL branch runs
  // unchanged and the code above is dead / tree-shaken.
  if (isClerkEnabled) {
    return (
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        appearance={ila26ClerkAppearance}
      >
        <ClerkLoading>
          <AppLoadingComponent />
        </ClerkLoading>
        <ClerkLoaded>
          <ClerkSessionSideEffects />
          <Show when="signed-in">{appBody}</Show>
          <Show when="signed-out">
            <SignInPage />
          </Show>
        </ClerkLoaded>
      </ClerkProvider>
    );
  }

  return (
    <MsalProvider instance={instance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
        loadingComponent={AppLoadingComponent}
      >
        {appBody}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

const AppWithLDProvider = withLDProvider({
  clientSideID: import.meta.env.VITE_APP_LAUNCHDARKLY_CLIENT_SIDE_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(App as ComponentType<object>);

export default AppWithLDProvider;
